"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin, Navigation } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const communities = [{
  id: "community-1",
  name: "Gaithersburg",
  address: "Montgomery County Service Area",
  city: "Gaithersburg, MD",
  description: "Roofing, siding, gutter, and storm damage services available for homeowners in Gaithersburg and nearby Montgomery County communities.",
  serviceLink: "/contact",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/gaithersburg_neighborhood.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/gaithersburg_neighborhood.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/gaithersburg_neighborhood.png-wvc-sizes",
  imageAlt: "Residential roofing project in Gaithersburg"
}, {
  id: "community-2",
  name: "Rockville",
  address: "Central Maryland Service Area",
  city: "Rockville, MD",
  description: "Reliable roof repair, roof replacement, siding, and gutter services for Rockville homeowners and surrounding neighborhoods.",
  serviceLink: "/contact",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/rockville_homes.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/rockville_homes.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/rockville_homes.png-wvc-sizes",
  imageAlt: "Siding replacement in Rockville"
}, {
  id: "community-3",
  name: "Germantown",
  address: "Upper Montgomery Service Area",
  city: "Germantown, MD",
  description: "Exterior services for Germantown homeowners, including roofing, siding, gutter installation, and storm damage support.",
  serviceLink: "/contact",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/germantown_exterior.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/germantown_exterior.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/germantown_exterior.png-wvc-sizes",
  imageAlt: "New roof installation in Germantown"
}, {
  id: "community-4",
  name: "Clarksburg",
  address: "Northern Corridor Service Area",
  city: "Clarksburg, MD",
  description: "Roofing and exterior services for Clarksburg homeowners, with support for roof repairs, replacements, gutters, and storm-related damage.",
  serviceLink: "/contact",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/clarksburg_roofing.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/clarksburg_roofing.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/clarksburg_roofing.png-wvc-sizes",
  imageAlt: "Exterior renovation in Clarksburg"
}, {
  id: "community-5",
  name: "Bethesda",
  address: "South Montgomery Service Area",
  city: "Bethesda, MD",
  description: "Premium roofing, siding, gutter, and storm damage services for Bethesda homeowners looking to protect and improve their homes.",
  serviceLink: "/contact",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/bethesda_residence.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/bethesda_residence.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/bethesda_residence.png-wvc-sizes",
  imageAlt: "Premium roofing in Bethesda"
}, {
  id: "community-6",
  name: "Silver Spring",
  address: "Eastern County Service Area",
  city: "Silver Spring, MD",
  description: "Dependable roofing and exterior services for Silver Spring homeowners, including roof repair, siding, gutters, and storm restoration.",
  serviceLink: "/contact",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/silver_spring_siding.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/silver_spring_siding.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/silver_spring_siding.png-wvc-sizes",
  imageAlt: "Gutter installation in Silver Spring"
}];
function AnimatedCommunityCard({
  community,
  index
}: {
  community: typeof communities[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div ref={ref} data-index={index} className={cn("transition-all duration-700 ease-out h-full", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="h-full flex flex-col overflow-hidden border-border/30 shadow-sm hover:shadow-md transition-shadow duration-300 rounded-md bg-muted/30">
        <div className="relative h-48 w-full overflow-hidden bg-muted">
          <img src={community.imageSrc} data-wvc-srcset={community.imageSrcset} data-wvc-sizes={community.imageSizes} alt={community.imageAlt} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" loading="lazy" />
        </div>

        <CardHeader className="pb-4">
          <CardTitle className="text-xl font-default text-primary font-semibold">
            {community.name}
          </CardTitle>
        </CardHeader>

        <CardContent className="flex-1 space-y-5">
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-secondary shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-foreground font-medium leading-none font-default">
                {community.address}
              </p>
              <p className="text-muted-foreground text-sm font-default">
                {community.city}
              </p>
            </div>
          </div>

          <Separator className="bg-border/40" />

          <p className="text-foreground text-sm font-default leading-relaxed">
            {community.description}
          </p>
          <p className="text-secondary text-sm font-medium font-default">
            Same-day or next-day consultations available
          </p>
        </CardContent>

        <CardFooter className="pt-4 pb-6">
          <Button asChild variant="outline" className="w-full group border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors rounded-md">
            <Link to="/ContactPage">
              <Navigation className="w-4 h-4 mr-2 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              Get Free Estimate for Your Area
            </Link>
          </Button>
        </CardFooter>
      </Card>
    </div>;
}
export default function FeaturedCommunitiesSection() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="254977" id="featured-communities-section" className="py-16 md:py-24 lg:py-32 bg-background text-foreground">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={cn("max-w-3xl mx-auto text-center mb-12 md:mb-16 transition-all duration-700 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-primary mb-4">
            Featured Communities
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            Eagle Exteriors proudly serves homeowners across Maryland and Northern Virginia with reliable roofing, siding, gutter, and storm damage services.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {communities.map((community, index) => <AnimatedCommunityCard key={community.id} community={community} index={index} />)}
        </div>
      </div>
    </section>;
}